import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { endpoints } from '../api/api';
import '../styles/Hero.css';

function Hero({ isMobile }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscriberCount, setSubscriberCount] = useState(1000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isWechat, setIsWechat] = useState(false);
  const mobileVideoRef = useRef(null);
  const desktopVideoRef = useRef(null);
  const heroRef = useRef(null);

  // 检测微信浏览器
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isWechatBrowser = ua.indexOf('micromessenger') !== -1;
    setIsWechat(isWechatBrowser);
    
    // 在控制台输出用户代理，帮助调试
    console.log('User Agent:', ua);
  }, []);

  // 根据当前语言决定显示th还是位
  const getNumberSuffix = () => {
    return i18n.language === 'zh' ? '位' : <sup>th</sup>;
  };

  useEffect(() => {
    // 在组件挂载时获取当前订阅用户数
    fetchSubscriberCount();
    
    // 检测是否为小尺寸移动设备
    const checkScreenSize = () => {
      setIsSmallMobile(window.innerWidth <= 576);
    };
    
    // 初始检查
    checkScreenSize();
    
    // 添加窗口大小变化监听器
    window.addEventListener('resize', checkScreenSize);
    
    // 清理监听器
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 处理视频加载事件
  useEffect(() => {
    const handleVideoLoad = () => {
      setVideoLoaded(true);
      console.log('Video loaded successfully');
    };

    const handleVideoError = (e) => {
      console.error('Video loading error:', e);
    };

    // 如果存在视频引用，添加事件监听器
    if (isSmallMobile && mobileVideoRef.current) {
      mobileVideoRef.current.addEventListener('loadeddata', handleVideoLoad);
      mobileVideoRef.current.addEventListener('error', handleVideoError);
      
      // 尝试手动播放视频，无需用户交互
      const playVideo = async () => {
        if (mobileVideoRef.current) {
          try {
            // 在微信浏览器中，我们仅在用户交互后尝试播放
            if (isWechat) {
              console.log('Wechat browser detected, video will play after user interaction');
              return;
            }

            // 使用低音量播放视频（实质静音）以绕过移动浏览器自动播放限制
            mobileVideoRef.current.volume = 0.001;
            await mobileVideoRef.current.play();
            // 播放成功后完全静音
            mobileVideoRef.current.volume = 0;
            mobileVideoRef.current.muted = true;
            console.log('移动端视频自动播放成功');
          } catch (err) {
            console.log('移动端视频自动播放失败，尝试使用静音模式:', err);
            // 如果普通播放失败，尝试使用静音模式播放
            mobileVideoRef.current.muted = true;
            try {
              await mobileVideoRef.current.play();
              console.log('移动端视频静音自动播放成功');
            } catch (muteErr) {
              console.log('移动端视频静音自动播放仍然失败:', muteErr);
            }
          }
        }
      };
      
      // 组件加载后立即尝试播放
      setTimeout(playVideo, 1000); // 延迟1秒加载视频，优化初始加载性能
      
      // 监听页面可见性变化，在页面变为可见时尝试播放视频
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          playVideo();
        }
      };
      
      // 添加用户交互事件，特别是对微信浏览器
      const handleUserInteraction = () => {
        if (isWechat && mobileVideoRef.current) {
          mobileVideoRef.current.muted = true;
          mobileVideoRef.current.play().catch(err => {
            console.log('微信浏览器视频播放失败:', err);
          });
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('touchstart', handleUserInteraction);
      document.addEventListener('click', handleUserInteraction);
      
      return () => {
        if (mobileVideoRef.current) {
          mobileVideoRef.current.removeEventListener('loadeddata', handleVideoLoad);
          mobileVideoRef.current.removeEventListener('error', handleVideoError);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.removeEventListener('touchstart', handleUserInteraction);
        document.removeEventListener('click', handleUserInteraction);
      };
    } else if (!isSmallMobile && desktopVideoRef.current) {
      desktopVideoRef.current.addEventListener('loadeddata', handleVideoLoad);
      desktopVideoRef.current.addEventListener('error', handleVideoError);
      
      return () => {
        if (desktopVideoRef.current) {
          desktopVideoRef.current.removeEventListener('loadeddata', handleVideoLoad);
          desktopVideoRef.current.removeEventListener('error', handleVideoError);
        }
      };
    }
  }, [isSmallMobile, isWechat]);

  // 处理滚动事件 - 大屏幕下控制内容显示/隐藏
  useEffect(() => {
    if (isSmallMobile) {
      // 移动端不做动态处理，直接显示内容
      setShowContent(true);
      return;
    }

    const handleScroll = () => {
      if (!heroRef.current) return;
      
      const heroElement = heroRef.current;
      const heroRect = heroElement.getBoundingClientRect();
      const heroHeight = heroElement.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // 判断用户是否已经向下滚动超过英雄区的5%（降低触发阈值）
      if (heroRect.top < -heroHeight * 0.01) {
        // 用户已向下滚动，显示内容
        setShowContent(true);
      } else if (heroRect.bottom > viewportHeight * 0.99) {
        // 用户滚回英雄区，隐藏内容
        setShowContent(false);
      }
    };
    
    // 初始不显示内容
    setShowContent(false);
    
    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll);
    
    // 清理监听器
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSmallMobile]);

  const fetchSubscriberCount = async () => {
    try {
      const response = await endpoints.subscriptions.getCount();
      if (response.data && response.data.count) {
        setSubscriberCount(response.data.count);
      }
    } catch (err) {
      console.error('Failed to fetch subscriber count:', err);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async () => {
    // 清除之前的消息
    setMessage('');
    setError('');

    // 验证邮箱
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await endpoints.subscriptions.create({
        email: email.trim()
      });

      if (response.data.success) {
        setMessage(response.data.message || 'Thank you for subscribing!');
        if (response.data.subscriberCount) {
          setSubscriberCount(response.data.subscriberCount);
        }
        setEmail(''); // 清空输入框
      }
    } catch (err) {
      console.error('Submit email failed:', err);
      setError(err.response?.data?.message || 'Submit failed, please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 获取等候者文本
  const getWaitlisterText = () => {
    if (i18n.language === 'zh') {
      return (
        <>
          <span>你将成为BOBO第</span>
          <span className={isSmallMobile ? "highlight-mobile" : "highlight"}>
            {subscriberCount}{getNumberSuffix()}
          </span>
          <span>等候者</span>
        </>
      );
    } else {
      return (
        <>
          <span>You will be the </span>
          <span className={isSmallMobile ? "highlight-mobile" : "highlight"}>
            {subscriberCount}{getNumberSuffix()}
          </span>
          <span> BOBO waitlister</span>
        </>
      );
    }
  };

  // 获取邮箱提示文本
  const getEmailNoticeText = () => {
    return i18n.language === 'zh'
      ? "输入邮箱获得专属优惠"
      : "Enter your email for exclusive offers";
  };

  // 移动端和桌面端不同的渲染内容
  const renderContent = () => {
    if (isSmallMobile) {
      return (
        <>
          <div className="hero-content hero-content-mobile">
            <div className="hero-subtitle hero-subtitle-mobile">
              {getWaitlisterText()}
            </div>
            <div className="email-input-container email-input-container-mobile">
              <input 
                type="email" 
                className="email-input email-input-mobile" 
                placeholder={getEmailNoticeText()} 
                value={email}
                onChange={handleEmailChange}
                disabled={isSubmitting}
              />
              <button 
                className="submit-button submit-button-mobile" 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Subscribe'}
              </button>
              <div className="email-notice email-notice-mobile">
                {getEmailNoticeText()}
              </div>
            </div>
            {message && <div className="success-message success-message-mobile">{message}</div>}
            {error && <div className="error-message error-message-mobile">{error}</div>}
          </div>
        </>
      );
    } else {
      // 桌面端渲染内容
      return (
        <div className={`hero-content hero-content-centered ${showContent ? 'hero-content-visible' : 'hero-content-hidden'}`}>
          <div className="hero-subtitle-single-line">
            {getWaitlisterText()}
          </div>
          <div className="email-input-container">
            <input 
              type="email" 
              className="email-input" 
              placeholder={getEmailNoticeText()} 
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
            />
            <button 
              className="submit-button" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Subscribe'}
            </button>
          </div>
          <div className="email-notice">
            {getEmailNoticeText()}
          </div>
          {message && <div className="success-message">{message}</div>}
          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }
  };

  const renderVideo = () => {
    // 在微信浏览器中，为移动端提供静态图片方案，而不是视频
    if (isSmallMobile && isWechat) {
      return (
        <div className="mobile-static-image">
          <img src="/images/hero.png" alt="BOBO" className="mobile-hero-image" />
        </div>
      );
    }
    
    // 标准移动端视频
    if (isSmallMobile) {
      return (
        <video 
          ref={mobileVideoRef}
          autoPlay 
          muted 
          loop 
          playsInline
          preload="auto"
          poster="/images/hero.png" 
          className="mobile-hero-video"
        >
          <source src="/videos/3.mp4" type="video/mp4; codecs=avc1.42E01E" />
          Your browser does not support the video tag.
        </video>
      );
    }
    
    // 桌面端视频
    return (
      <video 
        ref={desktopVideoRef}
        autoPlay 
        muted 
        loop 
        playsInline 
        preload="auto"
        poster="/images/hero.png"
        className="hero-video"
      >
        <source src="/videos/3.mp4" type="video/mp4; codecs=avc1.42E01E" />
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <>
      {isSmallMobile && (
        <section className="mobile-video-section">
          <div className="mobile-video-container">
            {renderVideo()}
          </div>
        </section>
      )}
      <section ref={heroRef} className={`hero ${isSmallMobile ? 'hero-mobile' : ''}`}>
        {!isSmallMobile && (
          <>
            <div className="video-background">
              <div className="video-container">
                {renderVideo()}
              </div>
            </div>
            <div className="hero-overlay"></div>
          </>
        )}
        {renderContent()}
      </section>
    </>
  );
}

export default Hero; 